import { useEffect, useState } from 'react';
import { VisitJourney } from '../components/VisitJourney';
import { PatientForm } from './Patients';
import { RecordPayment, NewInvoice } from './Finance';
import { ApiError, NetworkError, api, type TreatmentView } from '../api';
import { useI18n } from '../i18n';
import { AppointmentStatus, EmptyState, ErrorState, Skeleton, Tabs, Field, Modal, Spinner } from '../components';
import { ChartPanel } from '../components/ChartPanel';
import { ZoneChart } from '../components/ZoneChart';
import { ImagingUpload } from '../components/ImagingUpload';
import { SensorCapture } from '../components/SensorCapture';
import { isDesktop } from '../components/DesktopBar';
import { ImageViewer, type ViewerImage } from '../components/ImageViewer';
import { PrescribePanel } from '../components/PrescribePanel';
import { IconChevron, IconPlus } from '../icons';
import { initials } from '../App';
import { formatMinor, minorToInput, parseToMinor } from '../money';
import { describe } from '../errors';
import { ConfirmRemove } from '../components/ConfirmRemove';

type Detail = Awaited<ReturnType<typeof api.patient>>;

/**
 * One patient, whole.
 *
 * The chart used to be its own top-level section, which meant picking a patient
 * twice and reading their allergies on a different screen from the tooth you
 * were about to drill. Everything that belongs to a person now lives under
 * their name: the record, the chart, the treatments, the images, the
 * prescriptions, the account and the history.
 *
 * Two things stay fixed above the tabs, because they are true no matter which
 * tab you are on: WHO this is, and what would hurt them. Medical alerts are
 * never behind a tab.
 */
export function PatientRecord({
  patientId,
  permissions,
  specialties = ['dental'],
  features = [],
  onBack,
}: {
  patientId: string;
  permissions: string[];
  /** What the clinic practises: decides which charts the record shows. */
  specialties?: string[];
  /** The plan's features: the face and body chart needs `derma_aesthetics`. */
  features?: string[];
  onBack: () => void;
}) {
  const { t, tx, formatDate, personName, ageFrom } = useI18n();
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState('overview');
  const [removing, setRemoving] = useState<'archive' | 'restore' | 'delete' | null>(null);
  const [nonce, setNonce] = useState(0);

  const can = (p: string) => permissions.includes(p);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    api
      .patient(patientId)
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof NetworkError ? t.errors.network : e instanceof ApiError ? e.message : t.common.error);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, t, nonce]);

  if (error) return <ErrorState message={error} onRetry={onBack} />;

  const name = data ? personName(data.patient) : '';
  const age = data?.patient.birthDate ? ageFrom(data.patient.birthDate) : null;

  return (
    <>
      <button type="button" className="back-link" onClick={onBack}>
        <IconChevron size={14} className="chev" style={{ transform: 'scaleX(-1)' }} />
        {t.patients.title}
      </button>

      {!data ? (
        <div className="card" style={{ marginTop: 16 }}>
          <Skeleton rows={6} />
        </div>
      ) : (
        <>
          <div className="record-head">
            <span className="avatar lg">{initials(name)}</span>
            <div className="identity">
              <h1>
                {name}
                {/* An archived patient looks exactly like an active one unless
                    the record says so, which is how someone ends up calling a
                    patient who left. */}
                {data.patient.archived ? (
                  <span className="badge" style={{ marginInlineStart: 10, verticalAlign: 'middle' }}>
                    {t.patients.archived}
                  </span>
                ) : null}
              </h1>
              <p>
                <span className="ltr-value">{data.patient.patientNumber}</span>
                {data.patient.birthDate ? (
                  <>
                    {' · '}
                    {formatDate(data.patient.birthDate)}
                    {age !== null ? ` · ${tx(t.common.years, { n: age })}` : ''}
                  </>
                ) : null}
                {data.patient.phone ? (
                  <>
                    {' · '}
                    <span className="ltr-value">{data.patient.phone}</span>
                  </>
                ) : null}
              </p>
            </div>

            {/* Archiving sits beside the name rather than in a menu, and
                deleting sits beside it looking dangerous. Archive is the
                everyday act and is offered first. */}
            <div className="row" style={{ gap: 8, marginInlineStart: 'auto' }}>
              {can('patient.archive') ? (
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  onClick={() => setRemoving(data.patient.archived ? 'restore' : 'archive')}
                >
                  {data.patient.archived ? t.remove.restore : t.remove.archive}
                </button>
              ) : null}
              {can('patient.delete') && !data.patient.archived ? (
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => setRemoving('delete')}
                >
                  {t.remove.delete}
                </button>
              ) : null}
            </div>
          </div>

          {removing === 'delete' ? (
            <ConfirmRemove
              title={t.remove.deletePatient}
              what={`${name} · ${data.patient.patientNumber}`}
              body={
                <>
                  <p style={{ margin: 0, color: 'var(--ink-3)' }}>{t.remove.deletePatientBody}</p>
                  <p style={{ margin: 0, color: 'var(--danger)', fontWeight: 600 }}>
                    {t.remove.cannotUndo}
                  </p>
                </>
              }
              confirmWord={data.patient.patientNumber}
              actionLabel={t.remove.delete}
              onConfirm={() => api.deletePatient(patientId)}
              onClose={() => setRemoving(null)}
              onDone={() => {
                setRemoving(null);
                onBack();
              }}
            />
          ) : null}

          {removing === 'archive' || removing === 'restore' ? (
            <ConfirmRemove
              title={removing === 'archive' ? t.remove.archivePatient : t.remove.restorePatient}
              what={`${name} · ${data.patient.patientNumber}`}
              body={
                removing === 'archive' ? (
                  <p style={{ margin: 0, color: 'var(--ink-3)' }}>{t.remove.archivePatientBody}</p>
                ) : undefined
              }
              danger={false}
              actionLabel={removing === 'archive' ? t.remove.archive : t.remove.restore}
              onConfirm={async () => {
                await api.archivePatient(patientId, removing === 'archive');
              }}
              onClose={() => setRemoving(null)}
              onDone={() => {
                setRemoving(null);
                setNonce((n) => n + 1);
              }}
            />
          ) : null}

          {/* Medical alerts come first and are impossible to miss: they must be
              visible while charting, prescribing and recording anaesthetic
              (spec §6). Each shows the actual diagnosis and its context — never
              a vague flag, and never an automatic treatment rule. */}
          {data.alerts.length > 0 ? (
            <div className="stack" style={{ gap: 8, marginBottom: 20 }}>
              {data.alerts.map((a) => (
                <div key={a.id} className={a.severity === 'high' ? 'notice danger' : 'notice warn'} role="note">
                  <div>
                    <strong>{a.diagnosis}</strong>
                    {a.context ? <div style={{ marginTop: 3, fontSize: 12.5, opacity: 0.92 }}>{a.context}</div> : null}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <Tabs
            value={tab}
            onChange={setTab}
            tabs={[
              { value: 'overview', label: t.patients.overview },
              { value: 'visits', label: t.journey.tab, hidden: !can('clinical.chart.read') },
              { value: 'chart', label: t.chart.title, hidden: !can('clinical.chart.read') || !specialties.includes('dental') },
              {
                value: 'zones',
                label: t.zones.title,
                hidden: !can('clinical.chart.read') || !features.includes('derma_aesthetics') || !specialties.some((x) => x !== 'dental'),
              },
              { value: 'treatments', label: t.chart.treatments, hidden: !can('clinical.chart.read') },
              { value: 'imaging', label: t.imaging.title, hidden: !can('imaging.read') },
              { value: 'lab', label: t.lab.title, hidden: !can('lab.read') },
              { value: 'prescriptions', label: t.rx.title, hidden: !can('rx.draft') },
              {
                value: 'account',
                label: t.patients.account,
                hidden: !can('finance.patient_account.read'),
              },
              { value: 'timeline', label: t.patients.timeline, hidden: !can('clinical.notes.read') },
            ]}
          />

          {tab === 'overview' ? (
            <Overview
              data={data}
              canEditHistory={can('medical_history.write')}
              canEdit={can('patient.write')}
              onChanged={() => setNonce((n) => n + 1)}
            />
          ) : null}
          {tab === 'visits' ? <VisitJourney patientId={patientId} canWriteNotes={can('clinical.notes.write')} /> : null}
          {tab === 'chart' ? (
            <ChartPanel
              patientId={patientId}
              birthDate={data.patient.birthDate}
              canWrite={can('clinical.chart.write')}
            />
          ) : null}
          {tab === 'zones' ? (
            <ZoneChart patientId={patientId} canWrite={can('clinical.chart.write')} canUpload={can('imaging.upload')} specialties={specialties} />
          ) : null}
          {tab === 'treatments' ? <Treatments patientId={patientId} canWrite={can('clinical.chart.write')} /> : null}
          {tab === 'imaging' ? <Imaging patientId={patientId} canUpload={can('imaging.upload')} /> : null}
          {tab === 'lab' ? <LabWork patientId={patientId} /> : null}
          {tab === 'prescriptions' ? (
            <PrescribePanel patientId={patientId} patientName={name} canIssue={can('rx.issue')} />
          ) : null}
          {tab === 'account' ? (
            <Account
              patientId={patientId}
              canInvoice={can('finance.invoice.create')}
              canPay={can('finance.payment.record')}
            />
          ) : null}
          {tab === 'timeline' ? <Timeline patientId={patientId} /> : null}
        </>
      )}
    </>
  );
}

function Overview({
  data,
  canEditHistory,
  canEdit,
  onChanged,
}: {
  data: Detail;
  canEditHistory: boolean;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { t, tx, formatDate, formatTime, ageFrom, locale } = useI18n();
  const [editingHistory, setEditingHistory] = useState(false);
  const [editing, setEditing] = useState(false);
  const p = data.patient;
  const age = p.birthDate ? ageFrom(p.birthDate) : null;

  return (
    <div className="record-grid">
      <div>
        <div className="card">
          <div className="card-head">
            <h2>{t.patients.title}</h2>
            {canEdit ? (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(true)}>
                {t.patientEdit.edit}
              </button>
            ) : null}
          </div>
          {editing ? (
            <PatientForm
              patient={p}
              onClose={() => setEditing(false)}
              onSaved={() => {
                setEditing(false);
                onChanged();
              }}
            />
          ) : null}
          <div className="card-body">
            <dl className="detail-list">
              <Item label={t.patients.number} value={p.patientNumber} ltr />
              <Item
                label={t.patients.name}
                value={
                  p.givenNameAlt && p.familyNameAlt
                    ? `${p.givenName} ${p.familyName} · ${p.givenNameAlt} ${p.familyNameAlt}`
                    : `${p.givenName} ${p.familyName}`
                }
              />
              <Item
                label={t.patients.born}
                value={
                  p.birthDate
                    ? `${formatDate(p.birthDate)}${age !== null ? ` · ${tx(t.common.years, { n: age })}` : ''}`
                    : t.common.notRecorded
                }
              />
              <Item label={t.patients.sex} value={(t.sex as Record<string, string>)[p.sex ?? 'unspecified'] ?? '—'} />
              <Item label={t.patients.phone} value={p.phone ?? '—'} ltr />
              <Item label={t.patients.whatsapp} value={p.whatsappPhone ?? '—'} ltr />
              <Item label={t.patients.emailAddress} value={p.email ?? '—'} ltr />
              <Item
                label={t.sources.label}
                value={
                  [
                    p.sourceName ? (locale === 'ar' && p.sourceName.ar ? p.sourceName.ar : p.sourceName.en) : null,
                    p.referredByName ? t.sources.referredByOption.replace('{name}', p.referredByName) : null,
                    p.sourceDetail,
                  ]
                    .filter(Boolean)
                    .join(' · ') || t.sources.none
                }
              />
              {p.guardianName ? (
                <Item
                  label={t.patients.guardian}
                  value={`${p.guardianName}${p.guardianRelation ? ` (${p.guardianRelation})` : ''}${
                    p.guardianPhone ? ` · ${p.guardianPhone}` : ''
                  }`}
                />
              ) : null}
            </dl>
            {p.notes ? (
              <>
                <div className="section-label" style={{ marginTop: 20 }}>
                  {t.patients.notes}
                </div>
                <p className="preserve-text" style={{ marginTop: 6, marginBottom: 0 }}>
                  {p.notes}
                </p>
              </>
            ) : null}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>{t.patients.recentAppointments}</h2>
          </div>
          {data.appointments.length === 0 ? (
            <EmptyState title={t.appointments.none} />
          ) : (
            <div className="table-scroll">
              <table>
                <tbody>
                  {data.appointments.map((a) => (
                    <tr key={a.id}>
                      <td className="ltr-value" style={{ whiteSpace: 'nowrap' }}>
                        {formatDate(a.startsAt)} {formatTime(a.startsAt)}
                      </td>
                      <td>{a.reason ?? '—'}</td>
                      <td style={{ textAlign: 'end' }}>
                        <AppointmentStatus status={a.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>{t.patients.medicalHistory}</h2>
          {canEditHistory ? (
            <button type="button" className="btn btn-sm" onClick={() => setEditingHistory(true)}>
              {t.patients.editMedicalHistory}
            </button>
          ) : null}
        </div>
        <div className="card-body">
          {/* Three distinct states, never collapsed into a blank field. */}
          <span
            className={
              data.medicalHistory?.allergyStatus === 'positive'
                ? 'badge danger'
                : data.medicalHistory?.allergyStatus === 'none_known'
                  ? 'badge ok'
                  : 'badge warn'
            }
          >
            {(t.patients.allergyStatus as Record<string, string>)[data.medicalHistory?.allergyStatus ?? 'not_recorded']}
          </span>
          {data.medicalHistory?.allergyStatus === 'not_recorded' ? (
            <div style={{ color: 'var(--ink-3)', fontSize: 12.5, marginTop: 8 }}>{t.patients.allergyStatusHint}</div>
          ) : null}

          {data.allergies.length > 0 ? (
            <ul style={{ margin: '14px 0 0', paddingInlineStart: 18 }}>
              {data.allergies.map((a) => (
                <li key={a.id} style={{ marginBottom: 6 }}>
                  <strong>{a.substance}</strong>
                  {a.reaction ? ` — ${a.reaction}` : ''}{' '}
                  <span className="badge danger">{(t.severity as Record<string, string>)[a.severity] ?? a.severity}</span>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="section-label" style={{ marginTop: 16 }}>{t.patients.medications}</div>
          {(data.medicalHistory?.medications ?? []).length === 0 ? (
            <div style={{ color: 'var(--ink-3)', fontSize: 12.5 }}>{t.patients.noMedications}</div>
          ) : (
            <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>
              {(data.medicalHistory?.medications ?? []).map((m, i) => (
                <li key={`${m.name}-${i}`}>{m.name}</li>
              ))}
            </ul>
          )}
          {data.medicalHistory?.freeText ? (
            <p style={{ margin: '12px 0 0', whiteSpace: 'pre-wrap' }}>{data.medicalHistory.freeText}</p>
          ) : null}
          {data.medicalHistory?.reviewedAt ? (
            <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 10 }}>
              {t.patients.reviewedAt}: {formatDate(data.medicalHistory.reviewedAt)}
            </div>
          ) : null}
        </div>
      </div>

      {editingHistory ? (
        <MedicalHistoryEditor
          patientId={p.id}
          initial={{
            allergyStatus: (data.medicalHistory?.allergyStatus as 'not_recorded' | 'none_known' | 'positive' | undefined) ?? 'not_recorded',
            allergies: data.allergies.map((a) => ({ substance: a.substance, reaction: a.reaction ?? '', severity: a.severity })),
            medications: (data.medicalHistory?.medications ?? []).map((m) => m.name),
            freeText: data.medicalHistory?.freeText ?? '',
          }}
          onClose={() => setEditingHistory(false)}
          onDone={() => {
            setEditingHistory(false);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Allergies, current medicines and a note — the record the prescription
 * safety check reads. Every patient starts as "not recorded", which the
 * check refuses to prescribe across; this is where that changes, on purpose.
 */
function MedicalHistoryEditor({
  patientId,
  initial,
  onClose,
  onDone,
}: {
  patientId: string;
  initial: {
    allergyStatus: 'not_recorded' | 'none_known' | 'positive';
    allergies: { substance: string; reaction: string; severity: string }[];
    medications: string[];
    freeText: string;
  };
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const [status, setStatus] = useState(initial.allergyStatus);
  const [allergies, setAllergies] = useState(initial.allergies);
  const [medications, setMedications] = useState(initial.medications);
  const [freeText, setFreeText] = useState(initial.freeText);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cleanAllergies = allergies.filter((a) => a.substance.trim());
  // Listing an allergy and calling the status "none known" cannot both be true.
  const contradiction = status === 'none_known' && cleanAllergies.length > 0;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.updateMedicalHistory(patientId, {
        allergyStatus: cleanAllergies.length > 0 ? 'positive' : status,
        allergies: cleanAllergies.map((a) => ({ substance: a.substance.trim(), reaction: a.reaction.trim() || null, severity: a.severity })),
        medications: medications.filter((m) => m.trim()).map((m) => ({ name: m.trim() })),
        freeText: freeText.trim() || null,
      });
      onDone();
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t.patients.editMedicalHistory}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button type="button" className="btn" disabled={busy || contradiction} onClick={() => void submit()}>
            {busy ? <Spinner /> : t.chart.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}
        <div className="notice">
          <span>{t.patients.medicalHistoryHint}</span>
        </div>

        <Field label={t.patients.medicalHistory} required>
          <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} disabled={cleanAllergies.length > 0}>
            {(['not_recorded', 'none_known', 'positive'] as const).map((s) => (
              <option key={s} value={s}>
                {(t.patients.allergyStatus as Record<string, string>)[s]}
              </option>
            ))}
          </select>
        </Field>

        <div>
          <div className="section-label">{t.patients.allergies}</div>
          {allergies.map((a, i) => (
            <div key={i} className="row" style={{ gap: 8, marginTop: 8, alignItems: 'flex-end' }}>
              <Field label={t.patients.substance}>
                <input value={a.substance} onChange={(e) => setAllergies(allergies.map((x, j) => (j === i ? { ...x, substance: e.target.value } : x)))} />
              </Field>
              <Field label={t.patients.reaction}>
                <input value={a.reaction} onChange={(e) => setAllergies(allergies.map((x, j) => (j === i ? { ...x, reaction: e.target.value } : x)))} />
              </Field>
              <Field label={t.patients.severity}>
                <select value={a.severity} onChange={(e) => setAllergies(allergies.map((x, j) => (j === i ? { ...x, severity: e.target.value } : x)))}>
                  {(['mild', 'moderate', 'severe', 'unknown'] as const).map((s) => (
                    <option key={s} value={s}>{(t.severity as Record<string, string>)[s]}</option>
                  ))}
                </select>
              </Field>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAllergies(allergies.filter((_, j) => j !== i))}>
                {t.remove.confirm}
              </button>
            </div>
          ))}
          <button type="button" className="btn-link" style={{ marginTop: 8 }} onClick={() => setAllergies([...allergies, { substance: '', reaction: '', severity: 'unknown' }])}>
            + {t.patients.addAllergy}
          </button>
        </div>

        <div>
          <div className="section-label">{t.patients.medications}</div>
          {medications.map((m, i) => (
            <div key={i} className="row" style={{ gap: 8, marginTop: 8 }}>
              <input value={m} onChange={(e) => setMedications(medications.map((x, j) => (j === i ? e.target.value : x)))} style={{ flex: 1 }} />
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMedications(medications.filter((_, j) => j !== i))}>
                {t.remove.confirm}
              </button>
            </div>
          ))}
          <button type="button" className="btn-link" style={{ marginTop: 8 }} onClick={() => setMedications([...medications, ''])}>
            + {t.patients.addMedication}
          </button>
        </div>

        <Field label={t.patients.medicalNotes}>
          <textarea rows={3} value={freeText} onChange={(e) => setFreeText(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function Treatments({ patientId, canWrite }: { patientId: string; canWrite: boolean }) {
  const { t, locale, formatDate } = useI18n();
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.treatments>>['treatments'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [editing, setEditing] = useState<TreatmentView | null>(null);
  const [removing, setRemoving] = useState<TreatmentView | null>(null);

  useEffect(() => {
    setRows(null);
    api
      .treatments(patientId)
      .then((r) => setRows(r.treatments))
      .catch((e) => setError(describe(e, t)));
  }, [patientId, nonce, t]);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  return (
    <div className="card">
      <div className="card-head">
        <h2>
          {t.chart.treatments}
          {rows ? <span className="count-chip">{rows.length}</span> : null}
        </h2>
      </div>
      {rows === null ? (
        <Skeleton rows={4} />
      ) : rows.length === 0 ? (
        <EmptyState title={t.chart.noTreatments} />
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t.chart.treatments}</th>
                <th>{t.chart.tooth} · {t.zones.region}</th>
                <th>{t.common.status}</th>
                <th>{t.finance.amount}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.id}>
                  <td>
                    <strong>{locale === 'ar' ? x.nameAr : x.nameEn}</strong>
                    <span className="sub">{formatDate(x.createdAt)}</span>
                  </td>
                  <td className={x.zone ? undefined : 'ltr-value'}>
                    {x.zone ? ((t.zones.zone as Record<string, string>)[x.zone] ?? x.zone) : (x.toothFdi ?? '—')}
                    {x.surfaces.length > 0 ? ` (${x.surfaces.map((s) => (t.chart.surface as Record<string, string>)[s] ?? s).join('/')})` : ''}
                    {x.doseQty !== null && x.doseQty !== undefined
                      ? ` · ${x.doseQty} ${x.doseUnit ? ((t.zones.unit as Record<string, string>)[x.doseUnit] ?? x.doseUnit) : ''}`.trimEnd()
                      : ''}
                  </td>
                  <td>
                    <span className="badge">{(t.treatmentStatus as Record<string, string>)[x.status] ?? x.status}</span>
                  </td>
                  <td className="ltr-value">{formatMinor(x.priceMinor, x.currency, locale)}</td>
                  <td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
                    {/* A treatment could be recorded and never changed or
                        removed — an extraction entered on the wrong tooth was
                        permanent. Editable now; removable while it is not on an
                        invoice, with the chart put back. */}
                    {canWrite ? (
                      <>
                        <button
                          type="button"
                          className="btn-link"
                          style={{ marginInlineEnd: 12 }}
                          onClick={() => setEditing(x)}
                        >
                          {t.remove.edit}
                        </button>
                        {!x.billed ? (
                          <button
                            type="button"
                            className="btn-link danger"
                            style={{ marginInlineEnd: 12 }}
                            onClick={() => setRemoving(x)}
                          >
                            {t.remove.confirm}
                          </button>
                        ) : null}
                            {editing ? (
        <EditTreatment
          treatment={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {removing ? (
        <ConfirmRemove
          title={t.chart.removeTreatment}
          what={`${locale === 'ar' ? removing.nameAr : removing.nameEn}${removing.toothFdi ? ` · ${t.chart.tooth} ${removing.toothFdi}` : ''}`}
          body={<p style={{ margin: 0, color: 'var(--ink-3)' }}>{t.chart.removeTreatmentBody}</p>}
          actionLabel={t.remove.delete}
          onConfirm={() => api.deleteTreatment(removing.id)}
          onClose={() => setRemoving(null)}
          onDone={() => {
            setRemoving(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
    </>
                    ) : null}
                    {canWrite && (x.status === 'planned' || x.status === 'in_progress') ? (
                      <button
                        type="button"
                        className="btn-link"
                        disabled={busyId === x.id}
                        onClick={async () => {
                          setBusyId(x.id);
                          try {
                            await api.setTreatmentStatus(x.id, { status: 'completed' });
                            setNonce((n) => n + 1);
                          } catch (e) {
                            setError(describe(e, t));
                          } finally {
                            setBusyId(null);
                          }
                        }}
                      >
                        {t.chart.markCompleted}
                      </button>
                    ) : x.billed ? (
                      <span className="badge ok">{t.finance.billed}</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * The patient's whole imaging library.
 *
 * Charting could only ever attach an image to a tooth, so a panoramic or a CBCT
 * — which belong to the mouth, not to one tooth — had nowhere to go. They are
 * uploaded here, and images taken on a tooth appear here too: one stored file,
 * listed in both places, never two copies.
 */
function Imaging({ patientId, canUpload }: { patientId: string; canUpload: boolean }) {
  const { t, formatDate } = useI18n();
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.imagingLibrary>>['images'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [kindFilter, setKindFilter] = useState('');
  const [nonce, setNonce] = useState(0);
  const [viewing, setViewing] = useState<number | null>(null);

  useEffect(() => {
    setRows(null);
    api
      .imagingLibrary(patientId)
      .then((r) => setRows(r.images))
      .catch((e) => setError(describe(e, t)));
  }, [patientId, nonce, t]);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  const kinds = [...new Set((rows ?? []).map((r) => r.kind))].sort();
  const visible = kindFilter ? (rows ?? []).filter((r) => r.kind === kindFilter) : (rows ?? []);
  // Pictures open in the viewer, in the order shown; documents and DICOM studies open as files.
  const viewable: ViewerImage[] = visible
    .filter((img) => img.mimeType.startsWith('image/'))
    .map((img) => ({
      id: img.id,
      kind: img.kind,
      at: img.at,
      mimeType: img.mimeType,
      isSynthetic: img.isSynthetic,
      toothFdi: img.links.map((l) => l.toothFdi).find(Boolean) ?? null,
      zone: img.links.map((l) => l.zone).find(Boolean) ?? null,
    }));

  return (
    <>
      <div className="toolbar">
        {kinds.length > 1 ? (
          <select
            className="select-inline"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            aria-label={t.imaging.kind}
          >
            <option value="">{t.imaging.library}</option>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {(t.imaging.kinds as Record<string, string>)[k] ?? k}
              </option>
            ))}
          </select>
        ) : null}
        <div className="toolbar-spacer" />
        {canUpload && isDesktop() ? (
          <button type="button" className="btn btn-ghost" onClick={() => setCapturing(true)}>
            {t.capture.title}
          </button>
        ) : null}
        {canUpload ? (
          <button type="button" className="btn" onClick={() => setUploading(true)}>
            <IconPlus size={16} />
            {t.imaging.addImage}
          </button>
        ) : null}
      </div>

      <div className="card">
        <div className="card-head">
          <h2>
            {t.imaging.library}
            {rows ? <span className="count-chip">{visible.length}</span> : null}
          </h2>
        </div>
        {rows === null ? (
          <Skeleton rows={3} />
        ) : visible.length === 0 ? (
          <EmptyState
            title={t.imaging.noneYet}
            hint={t.chart.captureHint}
            action={
              canUpload ? (
                <button type="button" className="btn" onClick={() => setUploading(true)}>
                  {t.imaging.addImage}
                </button>
              ) : null
            }
          />
        ) : (
          <div className="card-body">
            <div className="thumb-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
              {visible.map((img) => {
                const inner = (
                  <>
                    {img.mimeType.startsWith('image/') ? (
                      <img src={`/api/imaging/file/${img.id}`} alt={img.kind} loading="lazy" />
                    ) : (
                      // A DICOM study or a PDF has no browser thumbnail. Saying
                      // what it is beats an empty grey box pretending to be one.
                      <span className="thumb-file">{img.mimeType === 'application/dicom' ? 'DICOM' : 'PDF'}</span>
                    )}
                    {img.isSynthetic ? <span className="thumb-tag warn">{t.chart.simulated}</span> : null}
                    <span className="thumb-meta">
                      <strong>{(t.imaging.kinds as Record<string, string>)[img.kind] ?? img.kind}</strong>
                      {formatDate(img.at)}
                      {img.links.some((l) => l.toothFdi) ? (
                        <span className="ltr-value">
                          {' \u00b7 '}
                          {img.links.map((l) => l.toothFdi).filter(Boolean).join(', ')}
                        </span>
                      ) : null}
                    </span>
                  </>
                );
                const at = viewable.findIndex((v) => v.id === img.id);
                return at >= 0 ? (
                  <button key={img.id} type="button" className="thumb thumb-btn" onClick={() => setViewing(at)}>
                    {inner}
                  </button>
                ) : (
                  <a key={img.id} className="thumb" href={`/api/imaging/file/${img.id}`} target="_blank" rel="noreferrer noopener">
                    {inner}
                  </a>
                );
              })}
            </div>
            <p className="hint" style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 14 }}>
              {t.imaging.dicomNote}
            </p>
          </div>
        )}
      </div>

      {viewing !== null && viewable[viewing] ? (
        <ImageViewer images={viewable} index={viewing} onClose={() => setViewing(null)} onIndexChange={setViewing} canAnnotate={canUpload} />
      ) : null}
      {uploading ? (
        <ImagingUpload
          patientId={patientId}
          onClose={() => setUploading(false)}
          onUploaded={() => {
            setUploading(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
      {capturing ? (
        <SensorCapture
          patientId={patientId}
          onClose={() => setCapturing(false)}
          onSaved={() => {
            setCapturing(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * This patient's laboratory work.
 *
 * A crown ordered from the lab and the planned crown in the chart are the same
 * piece of work, and they were two unrelated facts: the order lived in the
 * laboratory section and the treatment in the patient's chart, with no way to
 * ask "where is her crown?" from the file you were already looking at.
 *
 * The link existed in the schema from the start and was never populated. It is
 * now, and this is the answer to that question.
 */
function LabWork({ patientId }: { patientId: string }) {
  const { t, locale, formatDate } = useI18n();
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.labOrders>>['orders'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRows(null);
    api
      .labOrders({ patientId })
      .then((r) => setRows(r.orders))
      .catch((e) => setError(describe(e, t)));
  }, [patientId, t]);

  if (error) return <ErrorState message={error} />;

  return (
    <div className="card">
      <div className="card-head">
        <h2>
          {t.lab.orders}
          {rows ? <span className="count-chip">{rows.length}</span> : null}
        </h2>
      </div>
      {rows === null ? (
        <Skeleton rows={3} />
      ) : rows.length === 0 ? (
        <EmptyState title={t.lab.noneForPatient} hint={t.lab.raiseFromChart} />
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t.lab.item}</th>
                <th>{t.chart.tooth}</th>
                <th>{t.lab.laboratories}</th>
                <th>{t.lab.due}</th>
                <th>{t.common.status}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id}>
                  <td>
                    <strong>{o.itemType}</strong>
                    {o.shade ? <span className="sub">{o.shade}</span> : null}
                  </td>
                  <td className="ltr-value">{o.teeth.join(', ') || '—'}</td>
                  <td>{o.laboratory.name}</td>
                  <td>
                    {o.dueOn ? formatDate(o.dueOn) : '—'}
                    {o.overdue ? (
                      <span className="badge danger" style={{ marginInlineStart: 6 }}>
                        {t.lab.overdue}
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <span className="badge info">
                      {(t.lab.states as Record<string, string>)[o.status] ?? o.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="card-body" style={{ borderTop: '1px solid var(--line-2)', paddingTop: 14 }}>
        <p className="hint" style={{ color: 'var(--ink-3)', fontSize: 12, margin: 0 }}>
          {locale === 'ar' ? t.lab.patientCostNote : t.lab.patientCostNote}
        </p>
      </div>
    </div>
  );
}

function Account({
  patientId,
  canInvoice,
  canPay,
}: {
  patientId: string;
  canInvoice: boolean;
  canPay: boolean;
}) {
  const { t, locale, formatDate } = useI18n();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.patientAccount>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [paying, setPaying] = useState(false);
  const [invoicing, setInvoicing] = useState(false);

  useEffect(() => {
    setData(null);
    api
      .patientAccount(patientId)
      .then(setData)
      .catch((e) => setError(describe(e, t)));
  }, [patientId, t, nonce]);

  if (error) return <ErrorState message={error} />;
  if (!data) {
    return (
      <div className="card">
        <Skeleton rows={4} />
      </div>
    );
  }

  const billed = data.invoices.reduce((s, i) => s + i.totalMinor, 0);
  const paid = data.invoices.reduce((s, i) => s + i.paidMinor, 0);

  return (
    <>
      {/* The two acts of money, from the file you are already in: the same
          forms finance uses, aimed at this patient. Recording a payment used
          to mean leaving the patient to find them again in another section. */}
      {canPay || canInvoice ? (
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
          {canInvoice && data.unbilled.count > 0 ? (
            <button type="button" className="btn btn-ghost" onClick={() => setInvoicing(true)}>
              {t.finance.invoiceNow}
            </button>
          ) : null}
          {canPay ? (
            <button type="button" className="btn" onClick={() => setPaying(true)}>
              {data.outstandingMinor > 0 ? t.finance.settleAll : `+ ${t.finance.recordPayment}`}
            </button>
          ) : null}
        </div>
      ) : null}

      {paying ? (
        <RecordPayment
          forPatientId={patientId}
          onClose={() => setPaying(false)}
          onDone={() => {
            setPaying(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
      {invoicing ? (
        <NewInvoice
          initialPatientId={patientId}
          onClose={() => setInvoicing(false)}
          onCreated={() => {
            setInvoicing(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {/* Billed, paid and outstanding are three different measurements and are
          labelled as such rather than merged into one "balance". */}
      <div className="stats">
        <Stat label={t.finance.billed} value={formatMinor(billed, data.currency, locale)} tone="blue" />
        <Stat label={t.finance.collected} value={formatMinor(paid, data.currency, locale)} tone="teal" />
        <Stat
          label={t.finance.outstanding}
          value={formatMinor(data.outstandingMinor, data.currency, locale)}
          tone="amber"
        />
        {/* The fourth number, which "balance" alone hides: work done for this
            patient that no invoice has yet asked them to pay for. */}
        <Stat
          label={t.finance.unbilled}
          value={formatMinor(data.unbilled.totalMinor, data.currency, locale)}
          tone={data.unbilled.count > 0 ? 'violet' : 'teal'}
        />
        {/* Money paid ahead of any invoice. It is applied to the next one
            posted; until then it is said here rather than hidden. */}
        {data.creditMinor > 0 ? (
          <Stat label={t.finance.creditOnAccount} value={formatMinor(data.creditMinor, data.currency, locale)} tone="teal" />
        ) : null}
      </div>

      {data.unbilled.count > 0 ? (
        <div className="card">
          <div className="card-head">
            <h2>
              {t.finance.unbilled}
              <span className="count-chip">{data.unbilled.count}</span>
            </h2>
          </div>
          <div className="table-scroll">
            <table>
              <tbody>
                {data.unbilled.items.map((u) => (
                  <tr key={u.id}>
                    <td>{formatDate(u.performedAt)}</td>
                    <td>
                      {locale === 'ar' ? u.nameAr : u.nameEn}
                      {u.toothFdi ? ` · ${t.chart.tooth} ${u.toothFdi}` : ''}
                    </td>
                    <td className="ltr-value" style={{ textAlign: 'end' }}>
                      {formatMinor(u.priceMinor, u.currency, locale)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card-body" style={{ borderTop: '1px solid var(--line-2)', paddingTop: 12 }}>
            <p className="hint" style={{ color: 'var(--ink-3)', fontSize: 12, margin: 0 }}>
              {t.finance.unbilledHint}
            </p>
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="card-head">
          <h2>{t.finance.invoices}</h2>
        </div>
        {data.invoices.length === 0 ? (
          <EmptyState title={t.finance.noInvoices} />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.finance.invoiceNumber}</th>
                  <th>{t.common.status}</th>
                  <th>{t.finance.billed}</th>
                  <th>{t.finance.collected}</th>
                  <th>{t.finance.outstanding}</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((i) => (
                  <tr key={i.id}>
                    <td className="ltr-value">
                      {i.number ?? '—'}
                      <span className="sub">{i.issuedAt ? formatDate(i.issuedAt) : ''}</span>
                    </td>
                    <td>
                      <span className={`badge ${i.status === 'posted' ? 'ok' : ''}`}>{i.status === 'posted' ? t.finance.posted : i.status === 'draft' ? t.finance.draft : i.status}</span>
                    </td>
                    <td className="ltr-value">{formatMinor(i.totalMinor, data.currency, locale)}</td>
                    <td className="ltr-value">{formatMinor(i.paidMinor, data.currency, locale)}</td>
                    <td className="ltr-value">{formatMinor(i.outstandingMinor, data.currency, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.payments.length > 0 ? (
        <div className="card">
          <div className="card-head">
            <h2>{t.finance.payments}</h2>
          </div>
          <div className="table-scroll">
            <table>
              <tbody>
                {data.payments.map((p) => (
                  <tr key={p.id}>
                    <td>{formatDate(p.at)}</td>
                    <td>{(t.finance.methods as Record<string, string>)[p.method] ?? p.method}</td>
                    <td className="ltr-value" style={{ textAlign: 'end' }}>
                      {formatMinor(p.amountMinor, p.currency, locale)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
}

function Timeline({ patientId }: { patientId: string }) {
  const { t, formatDate, formatTime } = useI18n();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.timeline>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    api
      .timeline(patientId)
      .then(setData)
      .catch((e) => setError(describe(e, t)));
  }, [patientId, t]);

  if (error) return <ErrorState message={error} />;
  if (!data) {
    return (
      <div className="card">
        <Skeleton rows={5} />
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>{t.patients.timeline}</h2>
      </div>
      {data.events.length === 0 ? (
        <EmptyState title={t.patients.noHistory} />
      ) : (
        <div className="card-body">
          <div className="timeline">
            {data.events.map((e, i) => (
              <article key={`${e.at}-${i}`}>
                <span className="timeline-dot" />
                <strong>{e.title}</strong>
                {e.detail ? <p className="preserve-text">{e.detail}</p> : null}
                <small>
                  {formatDate(e.at)} {formatTime(e.at)}
                  {e.actor ? ` · ${e.actor}` : ''}
                </small>
              </article>
            ))}
          </div>
          {/* Say what is missing rather than showing a shorter list as if it
              were the whole story. */}
          {data.filtered.finance || data.filtered.notes ? (
            <p className="hint" style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 14 }}>
              {t.patients.timelineFiltered}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div className="card stat">
      <div className="stat-top">
        <span>{label}</span>
        <span className={`stat-icon ${tone}`} aria-hidden="true">
          ●
        </span>
      </div>
      <div className="stat-value ltr-value">{value}</div>
    </div>
  );
}

function Item({ label, value, ltr = false }: { label: string; value: string; ltr?: boolean }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={ltr ? 'ltr-value' : undefined}>{value}</dd>
    </div>
  );
}

/**
 * Correct a treatment.
 *
 * Price, dentist, date, notes and chargeability are open on every treatment.
 * The procedure and the tooth are fixed once the work is completed, because
 * the chart was written from them — the API refuses that change, and this
 * form does not offer it, so the refusal is never a surprise.
 */
function EditTreatment({ treatment, onClose, onDone }: { treatment: TreatmentView; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const [dentists, setDentists] = useState<{ membershipId: string; name: string }[]>([]);
  const initial = {
    price: minorToInput(treatment.priceMinor, treatment.currency),
    dentist: treatment.dentistMembershipId ?? '',
    performedOn: treatment.completedAt ? String(treatment.completedAt).slice(0, 10) : '',
    notes: treatment.notes ?? '',
    billable: treatment.billable,
    toothFdi: treatment.toothFdi ?? '',
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const completed = treatment.status === 'completed';

  useEffect(() => {
    api.dentists().then((r) => setDentists(r.dentists.map((d) => ({ membershipId: d.membershipId, name: d.name })))).catch(() => setDentists([]));
  }, []);

  const patch: Record<string, unknown> = {};
  const price = parseToMinor(form.price, treatment.currency);
  if (price !== null && price !== treatment.priceMinor) patch.priceMinor = price;
  if (form.dentist !== initial.dentist) patch.dentistMembershipId = form.dentist || null;
  if (form.performedOn !== initial.performedOn) patch.performedAt = form.performedOn ? `${form.performedOn}T12:00:00.000Z` : null;
  if (form.notes !== initial.notes) patch.notes = form.notes || null;
  if (form.billable !== initial.billable) patch.billable = form.billable;
  if (!completed && form.toothFdi !== initial.toothFdi) patch.toothFdi = form.toothFdi || null;

  return (
    <Modal
      title={`${t.remove.edit} — ${treatment.nameEn}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || Object.keys(patch).length === 0}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.updateTreatment(treatment.id, patch);
                onDone();
              } catch (e) {
                setError(describe(e, t));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Spinner /> : t.chart.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}
        {completed ? <div className="notice"><span>{t.chart.completedIsFixed}</span></div> : null}
        <div className="grid-2">
          <Field label={`${t.chart.price} (${treatment.currency})`} hint={treatment.billed ? t.chart.priceAfterBilled : undefined}>
            <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="ltr-value" inputMode="decimal" />
          </Field>
          <Field label={t.appointments.dentist}>
            <select value={form.dentist} onChange={(e) => setForm({ ...form, dentist: e.target.value })}>
              <option value="">—</option>
              {dentists.map((d) => (
                <option key={d.membershipId} value={d.membershipId}>{d.name}</option>
              ))}
            </select>
          </Field>
          {!completed ? (
            <Field label={t.chart.tooth} hint={t.patients.optional}>
              <input value={form.toothFdi} onChange={(e) => setForm({ ...form, toothFdi: e.target.value })} className="ltr-value" />
            </Field>
          ) : null}
          <Field label={t.chart.performedOn}>
            <input type="date" value={form.performedOn} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setForm({ ...form, performedOn: e.target.value })} className="ltr-value" />
          </Field>
        </div>
        <label className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
          <input type="checkbox" checked={form.billable} disabled={treatment.billed} onChange={(e) => setForm({ ...form, billable: e.target.checked })} style={{ marginTop: 3 }} />
          <span>
            <strong>{t.chart.chargeable}</strong>
            <span style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12 }}>{t.chart.chargeableHint}</span>
          </span>
        </label>
        <Field label={t.patients.notes} hint={t.patients.optional}>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
        </Field>
      </div>
    </Modal>
  );
}
